import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ExpenseVoucherContext = createContext();
export const useExpenseVouchers = () => useContext(ExpenseVoucherContext);

export const ExpenseVoucherProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);

  const mountedRef = useRef(true);

  const ENDPOINT = "/expense-vouchers";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- MOUNT SAFETY ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetVouchers = (value) => {
    if (mountedRef.current) setVouchers(value);
  };

  /* ---------------- FETCH ---------------- */
  const fetchVouchers = useCallback(async () => {
    if (!token) return;

    setLoading(true);

    try {
      const res = await fetch(`${baseURL}${ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(payload?.message || "Fetch failed");
      }

      const items =
        payload?.data || payload?.vouchers || payload || [];

      safeSetVouchers(items);

      return payload;
    } catch (err) {
      console.error("[Expense Vouchers] fetch error:", err);

      if (err.message?.toLowerCase().includes("token")) {
        logout?.();
      }

      safeSetVouchers([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL, logout]);

  /* ---------------- ADD ---------------- */
  const addVoucher = useCallback(
    async (data) => {
      try {
        const res = await fetch(`${baseURL}${ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchVouchers();
        return payload;
      } catch (err) {
        console.error("[Expense Vouchers] add error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchVouchers]
  );

  /* ---------------- UPDATE ---------------- */
  const updateVoucher = useCallback(
    async (id, data) => {
      try {
        const res = await fetch(`${baseURL}${ENDPOINT}/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchVouchers();
        return payload;
      } catch (err) {
        console.error("[Expense Vouchers] update error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchVouchers]
  );

  /* ---------------- DELETE ---------------- */
  const deleteVoucher = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${baseURL}${ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetVouchers((prev) =>
          prev.filter((v) => String(v.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[Expense Vouchers] delete error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* ---------------- STATUS TOGGLE (SAFE) ---------------- */
  const toggleStatus = useCallback(
    async (id, currentStatus) => {
      const newStatus =
        currentStatus === "active" ? "inactive" : "active";

      try {
        // optimistic update
        safeSetVouchers((prev) =>
          prev.map((v) =>
            String(v.id) === String(id)
              ? { ...v, status: newStatus }
              : v
          )
        );

        const res = await fetch(
          `${baseURL}${ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              status: newStatus,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Status failed");

        return payload;
      } catch (err) {
        console.error("[Expense Vouchers] status error:", err);

        // rollback
        safeSetVouchers((prev) =>
          prev.map((v) =>
            String(v.id) === String(id)
              ? { ...v, status: currentStatus }
              : v
          )
        );

        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchVouchers().catch(() => {});
  }, [isReady, token, fetchVouchers]);

  return (
    <ExpenseVoucherContext.Provider
      value={{
        vouchers,
        loading,
        fetchVouchers,
        addVoucher,
        updateVoucher,
        deleteVoucher,
        toggleStatus,
      }}
    >
      {children}
    </ExpenseVoucherContext.Provider>
  );
};